import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@components/button';
import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { Card } from '@components/ui/card';
import { EQueryKeys } from '@enums/queryKeys.enum';
import {
  createCollaboratorSchema,
  type CreateCollaboratorPayload,
} from '@services/collaborators/dtos/create.dto';
import { collaboratorService } from '@services/collaborators/service';
import { medicalSpecialtyService } from '@services/medicalSpecialties/service';

import { AddDocuments } from './@components/AddDocuments.dialog';
import { SelectMedicalSpecialties } from './@components/SelectMedicalSpecialties.dialog';

export const Route = createFileRoute('/_auth-layout/(auth)/register')({
  component: SignUp,
  loader: ({ context }) => {
    return context.queryClient.ensureQueryData({
      queryKey: [EQueryKeys.MedicalSpecialties],
      queryFn: () => medicalSpecialtyService.getAll(),
    });
  },
});

function SignUp() {
  const { list } = Route.useLoaderData();
  const navigate = useNavigate();

  const form = useForm<CreateCollaboratorPayload>({
    resolver: zodResolver(createCollaboratorSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      password: '',
      document: '',
      crm: '',
      documents: [],
      medicalSpecialties: [],
    },
  });

  const { isPending, mutate: register } = useMutation({
    mutationFn: collaboratorService.create,
    onError: () => {
      toast.error('Atenção', {
        description: 'Não foi possível criar a conta. Tente novamente',
      });
    },
    onSuccess: () => {
      toast.success('Sucesso', {
        description: 'Conta criada com sucesso! Sua conta está em análise.',
        duration: 5000,
      });
      form.reset({
        name: '',
        phone: '',
        email: '',
        password: '',
        document: '',
        crm: '',
        documents: [],
        medicalSpecialties: [],
      });
      navigate({ to: '/under-review', replace: true });
    },
  });

  const handleOnSubmit = form.handleSubmit(
    values => {
      register({
        name: values.name,
        email: values.email,
        phone: values.phone,
        password: values.password,
        crm: values.crm,
        document: values.document,
        documents: values.documents,
        medicalSpecialties: values.medicalSpecialties,
      });
    },
    errors => console.log('errors', errors),
  );

  return (
    <Form.Provider {...form}>
      <form onSubmit={handleOnSubmit}>
        <Card.Content className="mt-6 mb-6 p-0">
          <div className="space-y-2">
            <InputControlled
              design="auth"
              formMessageProps={{
                className: 'mb-0.5 ml-auto mt-0',
              }}
              name="name"
              placeholder="Nome completo"
              size="xl"
            />

            <InputControlled
              design="auth"
              formMessageProps={{
                className: 'mb-0.5 ml-auto mt-0',
              }}
              name="email"
              placeholder="E-mail"
              size="xl"
              type="email"
            />

            <InputControlled
              design="auth"
              format="(##) #####-####"
              formMessageProps={{
                className: 'mb-0.5 ml-auto mt-0',
              }}
              mask="_"
              name="phone"
              placeholder="Telefone"
              size="xl"
              variant="withPattern"
            />

            <div className="grid grid-cols-2 gap-x-2">
              <InputControlled
                design="auth"
                format="###.###.###-##"
                formMessageProps={{
                  className: 'mb-0.5 ml-auto mt-0',
                }}
                mask="_"
                name="document"
                placeholder="CPF"
                size="xl"
                variant="withPattern"
              />

              <InputControlled
                design="auth"
                formMessageProps={{
                  className: 'mb-0.5 ml-auto mt-0',
                }}
                maxLength={6}
                name="crm"
                placeholder="CRM"
                size="xl"
              />
            </div>

            <InputControlled
              design="auth"
              formMessageProps={{
                className: 'mb-0.5 ml-auto mt-0',
              }}
              name="password"
              placeholder="Senha"
              secure
              size="xl"
              type="password"
            />

            <SelectMedicalSpecialties list={list} />

            <AddDocuments />

            <Button
              className="mt-4 w-full shadow-none"
              disabled={isPending}
              isLoading={isPending}
              size="xl"
              type="submit">
              CADASTRAR
            </Button>
          </div>
        </Card.Content>
        <Card.Footer className="grid p-0">
          <p className="text-primary mt-8 text-center text-sm font-light">
            Ao clicar no botão "CADASTRAR", você reconhece que leu e aceita os{' '}
            <Link
              className="text-tertiary underline"
              preload={false}
              target="_blank"
              to="/terms-and-conditions">
              termos de uso
            </Link>{' '}
            e{' '}
            <Link
              className="text-tertiary underline"
              preload={false}
              target="_blank"
              to="/privacy-policy">
              políticas de privacidade
            </Link>{' '}
            da GPMED.
          </p>
        </Card.Footer>
      </form>
    </Form.Provider>
  );
}
